/**
 * M9 — Documentos con UX tipo Arca: chips de filtro por tipo de documento +
 * rejilla de tarjetas (antes: tabla densa). La lógica de datos (búsqueda
 * debounced, filtros en searchParams, paginación, resumen IA, descarga) se
 * conserva; cambia la presentación.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertCircle,
  Download,
  Eye,
  FileImage,
  FileSpreadsheet,
  FileText,
  Filter,
  Loader2,
  Mail,
  Search,
  Sparkles,
  Tag as TagIcon,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { toast } from '../lib/dialogs'
import { SELECTOR_PAGE_SIZE } from '../lib/constants'
import DocIndexBadge from '../components/cases/DocIndexBadge'
import AiResultModal from '../components/ui/AiResultModal'
import DocumentViewerModal from '../components/documents/DocumentViewerModal'
import {
  DocumentCategoryLabel,
  DocumentCategorySchema,
  type AiSummaryResponse,
  type ClientListResponse,
  type DocumentCategory,
  type DocumentListItem,
} from '@toga/shared'

const PAGE_SIZE = 48

interface ExtendedDoc extends DocumentListItem {
  opportunityCode?: string | null
  opportunityTitle?: string | null
  clientDisplayName?: string | null
}

interface ListResponse {
  items: ExtendedDoc[]
  total: number
  page: number
  pageSize: number
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Icono por tipo MIME (pdf/imagen/hoja de cálculo/correo/genérico). */
function FileTypeIcon({ mimeType, className }: { mimeType: string; className?: string }) {
  if (mimeType.startsWith('image/')) return <FileImage size={18} className={className} />
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return <FileSpreadsheet size={18} className={className} />
  if (mimeType.includes('message') || mimeType.includes('rfc822'))
    return <Mail size={18} className={className} />
  return <FileText size={18} className={className} />
}

export default function Documents() {
  const { t } = useTranslation(['documents', 'common', 'enums'])
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<ListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState(searchParams.get('q') ?? '')
  const [clients, setClients] = useState<Array<{ id: string; displayName: string }>>([])
  const [summarizingId, setSummarizingId] = useState<string | null>(null)
  // F44 — visor embebido (PDF/imagen/docx/xlsx/texto).
  const [viewer, setViewer] = useState<{ id: string; version: number; fileName: string } | null>(
    null,
  )
  const [aiResult, setAiResult] = useState<{
    title: string
    text: string
    conversationId: string | null
  } | null>(null)

  const category = searchParams.get('category') ?? ''
  const tag = searchParams.get('tag') ?? ''
  const clientId = searchParams.get('clientId') ?? ''
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1)

  // Etiqueta humana de categoría: i18n con fallback al label legado o al código.
  const catLabel = (c: string) =>
    t(`enums:DocumentCategory.${c}`, DocumentCategoryLabel[c as DocumentCategory] ?? c)

  // Clientes para el filtro (una sola carga).
  useEffect(() => {
    api
      .get<ClientListResponse>(`/clients?pageSize=${SELECTOR_PAGE_SIZE}&sortBy=name&sortDir=asc`)
      .then((r) => setClients(r.items.map((c) => ({ id: c.id, displayName: c.displayName }))))
      .catch(() => setClients([]))
  }, [])

  // Debounce búsqueda
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      if (q.trim()) next.set('q', q.trim())
      else next.delete('q')
      next.delete('page')
      setSearchParams(next, { replace: true })
    }, 300)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      const qParam = searchParams.get('q')
      if (qParam) params.set('q', qParam)
      if (category) params.set('category', category)
      if (tag) params.set('tag', tag)
      if (clientId) params.set('clientId', clientId)
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      const resp = await api.get<ListResponse>(`/documents?${params.toString()}`)
      setData(resp)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.load'))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [searchParams, category, tag, clientId, page, t])

  useEffect(() => {
    void load()
  }, [load])

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(searchParams)
    if (v) next.set(k, v)
    else next.delete(k)
    if (k !== 'page') next.delete('page')
    setSearchParams(next, { replace: true })
  }

  const onDownload = (d: ExtendedDoc) => {
    // Descarga directa vía API (las URLs prefirmadas de MinIO no son públicas).
    window.open(`/api/documents/${d.id}/versions/${d.latestVersion}/download`, '_blank')
  }

  const onSummarize = async (d: ExtendedDoc) => {
    setSummarizingId(d.id)
    try {
      const resp = await api.post<AiSummaryResponse>('/ai/summary', {
        contextType: 'DOCUMENT',
        contextId: d.id,
        style: 'BRIEF',
      })
      setAiResult({
        title: t('ai.summaryTitle', { fileName: d.fileName }),
        text: resp.text,
        conversationId: resp.conversationId,
      })
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string; code?: string }) : undefined
      if (body?.code === 'AI_NOT_CONFIGURED') {
        toast.error(t('errors.aiNotConfigured'))
      } else {
        toast.error(body?.error ?? (err instanceof Error ? err.message : t('errors.generic')))
      }
    } finally {
      setSummarizingId(null)
    }
  }

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data]
  )

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <FileText size={20} className="text-toga-700 dark:text-toga-300" />
            {t('title')}
          </h1>
          {data && (
            <p className="text-xs t-muted">
              {data.total} {data.total !== 1 ? t('countPlural') : t('countSingular')}
            </p>
          )}
        </div>
        <p className="text-xs t-faint">{t('uploadHint')}</p>
      </header>

      {/* Búsqueda + cliente + tag */}
      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
          />
        </div>
        <label className="text-xs t-muted flex items-center gap-2">
          <Filter size={12} className="t-faint" /> {t('filters.client')}
          <select
            value={clientId}
            onChange={(e) => setParam('clientId', e.target.value)}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
          >
            <option value="">{t('filters.allClients')}</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </label>
        {tag && (
          <button
            type="button"
            onClick={() => setParam('tag', '')}
            className="px-2 py-1 rounded text-xs border border-toga-500/40 bg-toga-500/15 text-toga-700 dark:text-toga-200 inline-flex items-center gap-1.5"
            title={t('filters.removeTag')}
          >
            <TagIcon size={11} /> {tag} ✕
          </button>
        )}
      </div>

      {/* M9 — chips de tipo de documento */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mb-1">
        <CategoryChip
          label={t('filters.allCategories')}
          active={category === ''}
          onClick={() => setParam('category', '')}
        />
        {DocumentCategorySchema.options.map((c) => (
          <CategoryChip
            key={c}
            label={catLabel(c)}
            active={category === c}
            onClick={() => setParam('category', category === c ? '' : c)}
          />
        ))}
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading && (
        <div className="surface p-8 text-center t-muted text-sm">
          <Loader2 size={16} className="inline-block animate-spin mr-2" /> {t('loading')}
        </div>
      )}

      {!loading && data && data.items.length === 0 && (
        <div className="surface p-12 text-center">
          <FileText size={32} className="mx-auto t-faint mb-3" />
          <p className="t-muted text-sm">{t('empty')}</p>
        </div>
      )}

      {/* M9 — rejilla de tarjetas */}
      {!loading && data && data.items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {data.items.map((d) => (
            <div key={d.id} className="surface p-3.5 flex flex-col gap-2.5 hover:border-toga-500/40 transition-colors">
              <div className="flex items-start gap-2.5 min-w-0">
                <span className="w-9 h-9 shrink-0 grid place-items-center rounded-lg bg-toga-500/10 text-toga-700 dark:text-toga-300">
                  <FileTypeIcon mimeType={d.latestMimeType} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="t-primary text-sm font-medium truncate" title={d.fileName}>
                    {d.fileName}
                  </p>
                  <button
                    type="button"
                    onClick={() => setParam('category', d.category)}
                    className="mt-0.5 inline-flex px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-[var(--bg-hover)] t-secondary hover:bg-toga-500/15"
                  >
                    {catLabel(d.category)}
                  </button>
                </div>
              </div>

              <div className="text-xs min-h-[16px]">
                {d.opportunityId && d.opportunityCode ? (
                  <Link
                    to={`/opportunities/${d.opportunityId}`}
                    className="text-toga-700 dark:text-toga-300 hover:underline"
                  >
                    <span className="font-mono">{d.opportunityCode}</span>
                    {d.opportunityTitle && (
                      <span className="t-muted"> · {d.opportunityTitle}</span>
                    )}
                  </Link>
                ) : d.clientDisplayName ? (
                  <span className="t-secondary">{d.clientDisplayName}</span>
                ) : (
                  <span className="t-faint">—</span>
                )}
              </div>

              {d.tags.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  {d.tags.slice(0, 4).map((tg) => (
                    <button
                      key={tg}
                      type="button"
                      onClick={() => setParam('tag', tg)}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-input-soft)] t-muted hover:bg-hover inline-flex items-center gap-1"
                    >
                      <TagIcon size={9} />
                      {tg}
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-auto pt-2 border-t border-base/50 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[11px] t-muted font-mono">
                  <span>{formatSize(d.latestSize)}</span>
                  <span className="t-faint">·</span>
                  <span>{new Date(d.createdAt).toLocaleDateString(currentLocaleTag())}</span>
                </div>
                <div className="flex items-center gap-1">
                  <DocIndexBadge status={d.indexStatus} />
                  <button
                    type="button"
                    onClick={() => void onSummarize(d)}
                    disabled={summarizingId !== null}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200 disabled:opacity-40"
                    title={t('actions.summarizeAi')}
                  >
                    {summarizingId === d.id ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <Sparkles size={13} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setViewer({ id: d.id, version: d.latestVersion, fileName: d.fileName })
                    }
                    className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200"
                    title={t('actions.view')}
                  >
                    <Eye size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void onDownload(d)}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200"
                    title={t('actions.download')}
                  >
                    <Download size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs t-muted">
          <span>{t('pagination.pageOf', { page: data.page, total: totalPages })}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={data.page <= 1}
              onClick={() => setParam('page', String(data.page - 1))}
              className="px-3 py-1 rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              {t('pagination.previous')}
            </button>
            <button
              type="button"
              disabled={data.page >= totalPages}
              onClick={() => setParam('page', String(data.page + 1))}
              className="px-3 py-1 rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              {t('pagination.next')}
            </button>
          </div>
        </div>
      )}

      {aiResult && (
        <AiResultModal
          title={aiResult.title}
          text={aiResult.text}
          conversationId={aiResult.conversationId}
          onClose={() => setAiResult(null)}
        />
      )}

      {viewer && (
        <DocumentViewerModal
          documentId={viewer.id}
          version={viewer.version}
          fileName={viewer.fileName}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  )
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-2.5 py-1 rounded-full text-xs whitespace-nowrap border transition-colors',
        active
          ? 'bg-toga-500/15 border-toga-500/40 text-toga-700 dark:text-toga-200 font-medium'
          : 'border-base t-muted hover:bg-hover hover:t-primary',
      ].join(' ')}
    >
      {label}
    </button>
  )
}
